"use client"

interface JwtPayload {
  [key: string]: any;
  SecurityContactUserFunctionalRoles?: string;
}

/**
 * Decodes a JWT token and returns the payload
 * Note: This only decodes the payload, it doesn't verify the signature
 */
export function decodeJwtToken(token: string): JwtPayload | null {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Extracts user functional roles from a JWT token
 * @param token JWT token string
 * @returns Array of role IDs as strings, or empty array if not found
 */
export function getUserRolesFromToken(token: string): string[] {
  const payload = decodeJwtToken(token);
  if (!payload) {
    console.warn('Could not decode JWT token for role extraction');
    return [];
  }

  const rolesString = payload.SecurityContactUserFunctionalRoles;
  if (!rolesString || typeof rolesString !== 'string') {
    console.warn('SecurityContactUserFunctionalRoles not found in token');
    return [];
  }

  // Split comma-separated roles and trim whitespace
  return rolesString.split(',').map(role => role.trim()).filter(Boolean);
}

/**
 * Checks if the user has at least one of the required roles
 * @param token JWT token string
 * @param requiredRoles Array of required role IDs (as strings)
 * @returns true if user has at least one required role, false otherwise
 */
export function hasRequiredRoles(token: string, requiredRoles: string[]): boolean {
  const userRoles = getUserRolesFromToken(token);
  if (userRoles.length === 0) {
    return false;
  }

  // Check if user has at least one of the required roles
  return requiredRoles.some(requiredRole => userRoles.includes(requiredRole));
}


